import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { APP_CONFIG } from '../config/constants';

// Déclaration TypeScript pour l'API hCaptcha
declare global {
  interface Window {
    hcaptcha: {
      render: (container: string | HTMLElement, params: HCaptchaParams) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      execute: (widgetId?: string) => Promise<{ response: string; key: string }>;
      getResponse: (widgetId?: string) => string;
    };
  }
}

interface HCaptchaParams {
  sitekey: string;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact' | 'invisible';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: (error: any) => void;
}

interface HCaptchaProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (error: any) => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
}

export interface HCaptchaHandle {
  reset: () => void;
}

export const HCaptcha = forwardRef<HCaptchaHandle, HCaptchaProps>(({
  onVerify,
  onExpire,
  onError,
  theme = 'light',
  size = 'normal',
}, ref) => {
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Utiliser des refs pour les callbacks afin d'éviter de recréer le widget
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    let isMounted = true;
    let widgetId: string | null = null;

    // Attendre que le script hCaptcha soit chargé
    const checkHCaptcha = setInterval(() => {
      if (window.hcaptcha && captchaRef.current && isMounted) {
        clearInterval(checkHCaptcha);

        // Vérifier si un widget n'existe pas déjà
        if (widgetIdRef.current) {
          return;
        }

        try {
          // Rendre le widget hCaptcha une seule fois
          widgetId = window.hcaptcha.render(captchaRef.current, {
            sitekey: APP_CONFIG.captcha.siteKey,
            theme,
            size,
            callback: (token: string) => {
              if (isMounted) onVerifyRef.current(token);
            },
            'expired-callback': () => {
              if (isMounted) onExpireRef.current?.();
            },
            'error-callback': (error: any) => {
              if (isMounted) onErrorRef.current?.(error);
            },
          });
          widgetIdRef.current = widgetId;
        } catch (error) {
          if (isMounted) onErrorRef.current?.(error);
        }
      }
    }, 100);

    // Nettoyage
    return () => {
      isMounted = false;
      clearInterval(checkHCaptcha);
      if (widgetId && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetId);
          widgetIdRef.current = null;
        } catch (error) {
          // Ignorer les erreurs de nettoyage silencieusement
        }
      }
    };
  }, [theme, size]);

  // Méthode publique pour réinitialiser le captcha
  const reset = () => {
    if (widgetIdRef.current && window.hcaptcha) {
      window.hcaptcha.reset(widgetIdRef.current);
    }
  };

  // Exposer la méthode reset via ref
  useImperativeHandle(ref, () => ({
    reset,
  }));

  return (
    <div className="flex justify-center">
      <div ref={captchaRef} id="hcaptcha-container"></div>
    </div>
  );
});

